/**
 * 统一的 Hooks 管理系统
 * 提供类似 Git hooks 的事件驱动框架
 */

import { EventEmitter } from 'events';
import fs from 'fs-extra';
import path from 'path';
import { exec } from 'child_process';
import { promisify } from 'util';
import { logger } from '../../utils/logger';
import { Task, TaskStatus } from '../../types';
import { ExtendedTask, isExtendedTask } from '../../types/extended-task';
import { PRDManager } from '../prd-manager';

const execAsync = promisify(exec);

export interface HookEvent {
  type: string;
  data: any;
  timestamp: Date;
  source?: string;
}

export interface HookHandler {
  name: string;
  type: 'script' | 'function';
  path?: string; // for script hooks
  handler?: (event: HookEvent) => Promise<void>; // for function hooks
  enabled: boolean;
  priority?: number;
}

export interface HookConfig {
  hooks: Record<string, HookHandler[]>;
  globalOptions?: {
    timeout?: number;
    parallel?: boolean;
    stopOnError?: boolean;
  };
}

// 预定义的 hook 类型
export enum HookType {
  // 任务相关
  TASK_CREATE = 'task:create',
  TASK_UPDATE = 'task:update',
  TASK_DELETE = 'task:delete',
  TASK_START = 'task:start',
  TASK_COMPLETE = 'task:complete',
  TASK_BLOCK = 'task:block',
  
  // 文档相关
  PRD_CREATE = 'prd:create',
  PRD_UPDATE = 'prd:update',
  DESIGN_CREATE = 'design:create',
  DESIGN_UPDATE = 'design:update',
  DESIGN_APPROVE = 'design:approve',
  
  // 工作流相关
  WORKFLOW_START = 'workflow:start',
  WORKFLOW_COMPLETE = 'workflow:complete',
  WORKFLOW_ERROR = 'workflow:error',
  
  // 文件系统相关
  FILE_CHANGE = 'file:change',
  FILE_CREATE = 'file:create',
  FILE_DELETE = 'file:delete',
  
  // 工具使用相关
  TOOL_BEFORE_USE = 'tool:before_use',
  TOOL_AFTER_USE = 'tool:after_use',
  
  // AI 相关
  AI_THINKING = 'ai:thinking',
  AI_RESPONSE = 'ai:response',
  AGENT_START = 'agent:start',
  AGENT_COMPLETE = 'agent:complete',
  
  // 系统相关
  SESSION_START = 'session:start',
  SESSION_END = 'session:end',
  ERROR = 'error',
}

export class HookManager extends EventEmitter {
  private config: HookConfig;
  private hooksDir: string;
  private configPath: string;
  private handlers: Map<string, HookHandler[]> = new Map();
  private prdManager: PRDManager;
  
  constructor(workflowDir?: string) {
    super();
    const projectRoot = workflowDir || process.cwd();
    this.hooksDir = path.join(projectRoot, '.workflow', 'hooks');
    this.configPath = path.join(this.hooksDir, 'config.json');
    this.config = { hooks: {} };
    this.prdManager = new PRDManager(projectRoot);
  }
  
  async initialize(): Promise<void> {
    // 确保 hooks 目录存在
    await fs.ensureDir(this.hooksDir);
    
    // 创建默认的 hook 目录
    const hookDirs = [
      'task',
      'document',
      'workflow',
      'file',
      'tool',
      'ai'
    ];
    
    for (const dir of hookDirs) {
      await fs.ensureDir(path.join(this.hooksDir, dir));
    }
    
    // 加载配置
    await this.loadConfig();
    
    // 创建示例 hooks
    await this.createExampleHooks();
    
    logger.info('Hook manager initialized');
  }
  
  /**
   * 注册 hook 处理器
   */
  register(hookType: string, handler: HookHandler): void {
    if (!this.handlers.has(hookType)) {
      this.handlers.set(hookType, []);
    }
    
    const handlers = this.handlers.get(hookType)!;
    handlers.push(handler);
    
    // 按优先级排序
    handlers.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    
    logger.debug(`Registered hook handler: ${handler.name} for ${hookType}`);
  }
  
  /**
   * 注册脚本 hook
   */
  async registerScript(hookType: string, scriptPath: string, options?: Partial<HookHandler>): Promise<void> {
    const handler: HookHandler = {
      name: path.basename(scriptPath),
      type: 'script',
      path: scriptPath,
      enabled: true,
      priority: 0,
      ...options
    };
    
    // 确保脚本存在且可执行
    if (await fs.pathExists(scriptPath)) {
      const stats = await fs.stat(scriptPath);
      if (stats.isFile()) {
        // 确保脚本可执行
        await fs.chmod(scriptPath, '755');
        this.register(hookType, handler);
      }
    } else {
      logger.warn(`Hook script not found: ${scriptPath}`);
    }
  }
  
  /**
   * 注册函数 hook
   */
  registerFunction(hookType: string, name: string, handler: (event: HookEvent) => Promise<void>, options?: Partial<HookHandler>): void {
    const hookHandler: HookHandler = {
      name,
      type: 'function',
      handler,
      enabled: true,
      priority: 0,
      ...options
    };
    
    this.register(hookType, hookHandler);
  }
  
  /**
   * 触发 hook
   */
  async trigger(hookType: string, data: any, options?: { source?: string; timeout?: number }): Promise<void> {
    const event: HookEvent = {
      type: hookType,
      data,
      timestamp: new Date(),
      source: options?.source
    };
    
    logger.debug(`Triggering hook: ${hookType}`);
    
    // 发出事件（用于内部监听）
    this.emit(hookType, event);
    
    // 获取该类型的所有处理器
    const handlers = this.handlers.get(hookType) || [];
    const configHandlers = this.config.hooks[hookType] || [];
    const allHandlers = [...handlers, ...configHandlers].filter(h => h.enabled);
    
    if (allHandlers.length === 0) {
      logger.debug(`No handlers registered for hook: ${hookType}`);
      return;
    }
    
    // 执行处理器
    const parallel = this.config.globalOptions?.parallel ?? false;
    const stopOnError = this.config.globalOptions?.stopOnError ?? false;
    const timeout = options?.timeout || this.config.globalOptions?.timeout || 30000;
    
    if (parallel) {
      // 并行执行
      const promises = allHandlers.map(handler => 
        this.executeHandler(handler, event, timeout).catch(error => {
          if (stopOnError) throw error;
          logger.error(`Hook handler ${handler.name} failed:`, error);
        })
      );
      await Promise.all(promises);
    } else {
      // 顺序执行
      for (const handler of allHandlers) {
        try {
          await this.executeHandler(handler, event, timeout);
        } catch (error) {
          logger.error(`Hook handler ${handler.name} failed:`, error);
          if (stopOnError) throw error;
        }
      }
    }
  }
  
  /**
   * 执行单个处理器
   */
  private async executeHandler(handler: HookHandler, event: HookEvent, timeout: number): Promise<void> {
    logger.debug(`Executing handler: ${handler.name}`);
    
    if (handler.type === 'function' && handler.handler) {
      // 执行函数处理器
      await Promise.race([
        handler.handler(event),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Handler timeout')), timeout)
        )
      ]);
    } else if (handler.type === 'script' && handler.path) {
      // 执行脚本处理器
      const env = {
        ...process.env,
        HOOK_TYPE: event.type,
        HOOK_DATA: JSON.stringify(event.data),
        HOOK_TIMESTAMP: event.timestamp.toISOString(),
        HOOK_SOURCE: event.source || ''
      };
      
      try {
        const { stdout, stderr } = await execAsync(handler.path, {
          env,
          timeout
        });
        
        if (stdout) logger.debug(`Hook output: ${stdout}`);
        if (stderr) logger.warn(`Hook stderr: ${stderr}`);
      } catch (error: any) {
        throw new Error(`Script execution failed: ${error.message}`);
      }
    }
  }
  
  /**
   * 加载配置
   */
  private async loadConfig(): Promise<void> {
    if (await fs.pathExists(this.configPath)) {
      try {
        this.config = await fs.readJson(this.configPath);
      } catch (error) {
        logger.error('Failed to load hook config:', error);
        this.config = { hooks: {} };
      }
    } else {
      // 创建默认配置
      await this.saveConfig();
    }
  }
  
  /**
   * 保存配置
   */
  private async saveConfig(): Promise<void> {
    await fs.writeJson(this.configPath, this.config, { spaces: 2 });
  }
  
  /**
   * 创建示例 hooks
   */
  private async createExampleHooks(): Promise<void> {
    // 任务完成 hook 示例
    const taskCompleteHook = `#!/bin/bash
# Hook: 任务完成时执行
# 环境变量:
#   HOOK_TYPE: 事件类型
#   HOOK_DATA: 事件数据 (JSON)
#   HOOK_TIMESTAMP: 时间戳

echo "Task completed hook triggered"
echo "Type: $HOOK_TYPE"
echo "Data: $HOOK_DATA"

# 解析任务数据
TASK_ID=$(echo "$HOOK_DATA" | jq -r '.id')
TASK_CONTENT=$(echo "$HOOK_DATA" | jq -r '.content')

# 记录到日志
echo "[$HOOK_TIMESTAMP] Task completed: $TASK_CONTENT (ID: $TASK_ID)" >> .workflow/logs/completed-tasks.log

# 发送通知（如果配置了）
# workflow notify "Task completed: $TASK_CONTENT"
`;

    const hookPath = path.join(this.hooksDir, 'task', 'on-complete.sh');
    if (!await fs.pathExists(hookPath)) {
      await fs.writeFile(hookPath, taskCompleteHook);
      await fs.chmod(hookPath, '755');
    }
    
    // 文件变更 hook 示例
    const fileChangeHook = `#!/bin/bash
# Hook: 文件变更时执行
# 可用于自动格式化、测试等

FILE_PATH=$(echo "$HOOK_DATA" | jq -r '.path')
FILE_TYPE=$(echo "$HOOK_DATA" | jq -r '.type')

echo "File changed: $FILE_PATH"

# 根据文件类型执行不同操作
case "$FILE_PATH" in
  *.ts|*.tsx)
    # TypeScript 文件：运行 prettier
    # npx prettier --write "$FILE_PATH"
    ;;
  *.md)
    # Markdown 文件：检查拼写
    # npx cspell "$FILE_PATH"
    ;;
esac
`;

    const fileHookPath = path.join(this.hooksDir, 'file', 'on-change.sh');
    if (!await fs.pathExists(fileHookPath)) {
      await fs.writeFile(fileHookPath, fileChangeHook);
      await fs.chmod(fileHookPath, '755');
    }
  }
  
  /**
   * 列出所有可用的 hooks
   */
  async listHooks(): Promise<Record<string, HookHandler[]>> {
    const result: Record<string, HookHandler[]> = {};
    
    // 添加注册的处理器
    for (const [type, handlers] of this.handlers) {
      result[type] = handlers;
    }
    
    // 添加配置中的处理器
    for (const [type, handlers] of Object.entries(this.config.hooks)) {
      if (!result[type]) {
        result[type] = [];
      }
      result[type].push(...handlers);
    }
    
    // 扫描 hooks 目录中的脚本
    const dirs = await fs.readdir(this.hooksDir);
    for (const dir of dirs) {
      const dirPath = path.join(this.hooksDir, dir);
      const stats = await fs.stat(dirPath);
      
      if (stats.isDirectory()) {
        const files = await fs.readdir(dirPath);
        for (const file of files) {
          if (file.endsWith('.sh') || file.endsWith('.js')) {
            const hookType = this.inferHookType(dir, file);
            if (!result[hookType]) {
              result[hookType] = [];
            }
            
            const scriptPath = path.join(dirPath, file);
            result[hookType].push({
              name: file,
              type: 'script',
              path: scriptPath,
              enabled: true
            });
          }
        }
      }
    }
    
    return result;
  }
  
  /**
   * 推断 hook 类型
   */
  private inferHookType(dir: string, filename: string): string {
    // 基于目录和文件名推断
    const base = `${dir}:`;
    
    if (filename.includes('create')) return base + 'create';
    if (filename.includes('update')) return base + 'update';
    if (filename.includes('delete')) return base + 'delete';
    if (filename.includes('complete')) return base + 'complete';
    if (filename.includes('start')) return base + 'start';
    if (filename.includes('change')) return base + 'change';
    
    return base + 'unknown';
  }
  
  /**
   * 启用/禁用 hook
   */
  async toggleHook(hookType: string, handlerName: string, enabled: boolean): Promise<void> {
    // 更新内存中的处理器
    const handlers = this.handlers.get(hookType);
    if (handlers) {
      const handler = handlers.find(h => h.name === handlerName);
      if (handler) {
        handler.enabled = enabled;
      }
    }
    
    // 更新配置
    if (this.config.hooks[hookType]) {
      const handler = this.config.hooks[hookType].find(h => h.name === handlerName);
      if (handler) {
        handler.enabled = enabled;
        await this.saveConfig();
      }
    }
    
    logger.info(`Hook ${handlerName} ${enabled ? 'enabled' : 'disabled'} for ${hookType}`);
  }
  
  /**
   * 集成任务系统的便捷方法
   */
  async onTaskCreate(task: Task): Promise<void> {
    await this.trigger(HookType.TASK_CREATE, task);
  }
  
  async onTaskUpdate(task: Task, changes: Partial<Task>): Promise<void> {
    await this.trigger(HookType.TASK_UPDATE, { task, changes });
    
    // 如果是扩展任务且有 PRD 关联，更新 PRD 进度
    if (isExtendedTask(task)) {
      const extTask = task as ExtendedTask;
      if (extTask.prdId && changes.status) {
        await this.prdManager.onTaskStatusChange(extTask);
      }
    }
  }
  
  async onTaskComplete(task: Task): Promise<void> {
    await this.trigger(HookType.TASK_COMPLETE, task);
    
    // 如果是扩展任务且有 PRD 关联，更新 PRD 进度
    if (isExtendedTask(task)) {
      const extTask = task as ExtendedTask;
      if (extTask.prdId) {
        await this.prdManager.onTaskStatusChange(extTask);
      }
    }
  }
  
  async onTaskStart(task: Task): Promise<void> {
    await this.trigger(HookType.TASK_START, task);
    
    // 如果是扩展任务且有 PRD 关联，更新 PRD 进度
    if (isExtendedTask(task)) {
      const extTask = task as ExtendedTask;
      if (extTask.prdId) {
        await this.prdManager.onTaskStatusChange(extTask);
      }
    }
  }
  
  /**
   * 集成文档系统的便捷方法
   */
  async onPRDCreate(prdPath: string, content: string): Promise<void> {
    await this.trigger(HookType.PRD_CREATE, { path: prdPath, content });
  }
  
  async onDesignCreate(designDoc: any): Promise<void> {
    await this.trigger(HookType.DESIGN_CREATE, designDoc);
  }
  
  async onDesignApprove(designId: string): Promise<void> {
    await this.trigger(HookType.DESIGN_APPROVE, { id: designId });
  }
}